import { useState } from "react";
import { Sparkles, ShieldCheck, ShieldAlert, ChevronDown, AlertTriangle, Wand2 } from "lucide-react";

const SEV = {
  hoy: { label: "Høy", cls: "bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30" },
  middels: { label: "Middels", cls: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30" },
  lav: { label: "Lav", cls: "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30" },
};

/**
 * Viser resultatet av AI-gjennomgangen (klausul-konflikter) for en signert aksjonæravtale.
 * check: { status, conflicts: [{clauses, severity, title, explanation}], summary }
 */
export function AiConflictReport({ check, testIdPrefix = "ai-conflict" }) {
  const [open, setOpen] = useState(false);
  if (!check) return null;
  const conflicts = check.conflicts || [];
  const hasConflicts = conflicts.length > 0;

  if (check.status === "pending") {
    return (
      <div className="mt-3 flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground" data-testid={`${testIdPrefix}-pending`}>
        <Sparkles className="h-3.5 w-3.5 animate-pulse" /> AI gjennomgår klausulene …
      </div>
    );
  }

  if (check.status === "error") {
    return (
      <div className="mt-3 flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground" data-testid={`${testIdPrefix}-error`}>
        <Sparkles className="h-3.5 w-3.5" /> AI-sjekk av klausuler ble ikke fullført.
      </div>
    );
  }

  if (!hasConflicts) {
    return (
      <div className="mt-3 flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/[0.08] px-3 py-2 text-xs font-medium text-emerald-700 dark:text-emerald-300" data-testid={`${testIdPrefix}-ok`}>
        <ShieldCheck className="h-4 w-4" /> AI: ingen motstridende klausuler funnet
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-lg border border-amber-500/40 bg-amber-500/[0.08]" data-testid={`${testIdPrefix}-warn`}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-xs font-semibold text-amber-800 dark:text-amber-200"
        data-testid={`${testIdPrefix}-toggle`}>
        <span className="flex items-center gap-2">
          <ShieldAlert className="h-4 w-4" /> AI: {conflicts.length} mulige {conflicts.length === 1 ? "konflikt" : "konflikter"} mellom klausuler
        </span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open ? (
        <div className="space-y-2 px-3 pb-3">
          {conflicts.map((c, i) => {
            const sev = SEV[c.severity] || SEV.middels;
            return (
              <div key={i} className="rounded-md border border-border bg-background/60 p-2.5" data-testid={`${testIdPrefix}-item-${i}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                  <span className="text-xs font-semibold">{c.title || "Mulig konflikt"}</span>
                  <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${sev.cls}`}>{sev.label}</span>
                  {(c.clauses || []).length ? (
                    <span className="text-[10px] font-medium text-muted-foreground">{(c.clauses || []).join(" ↔ ")}</span>
                  ) : null}
                </div>
                {c.explanation ? <p className="mt-1 text-xs text-muted-foreground">{c.explanation}</p> : null}
                {c.suggestion ? (
                  <div className="mt-2 rounded-md border border-primary/40 bg-primary/[0.06] p-2" data-testid={`${testIdPrefix}-suggestion-${i}`}>
                    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-primary">
                      <Wand2 className="h-3 w-3" /> AI-forslag til omskriving{c.target ? ` · ${c.target}` : ""}
                    </div>
                    <p className="mt-1 text-xs text-foreground">{c.suggestion}</p>
                  </div>
                ) : null}
              </div>
            );
          })}
          <p className="pt-1 text-[10px] text-muted-foreground">
            AI-generert veiledning – ikke juridisk rådgivning. Kontroller alltid med egen rådgiver.
          </p>
        </div>
      ) : null}
    </div>
  );
}
